import { Fragment, useState, MouseEvent, FC } from "react";
import { Button, IconButton, SvgIcon, Menu } from "@mui/material";

import "./style.scss";
import { ReactComponent as GearIcon } from "../../assets/gearIcon.svg";
import { ReactComponent as MoreIcon } from "../../assets/more-vertical.icon.svg";

export enum SettingMenuType {
    default,
    more
}

export enum SettingItemType {
    default,
    dangerous
}

export interface SettingItem {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    name: string;
    type: SettingItemType;
    handler: () => void;
}

export const SettingMenu = ({
    actions,
    type = SettingMenuType.default,
    className = ""
}: {
    actions: SettingItem[];
    type?: SettingMenuType;
    className?: string;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuItem = (handler: () => void) => {
        handler();
        handleMenuClose();
    };

    return (
        <Fragment>
            {type === SettingMenuType.more ? (
                <div className={`more-menu-icon-button ${className}`} onClick={handleMenuClick}>
                    <SvgIcon component={MoreIcon} inheritViewBox />
                </div>
            ) : (
                <IconButton
                    className={`setting-menu-icon-button ${className}`}
                    onClick={handleMenuClick}
                >
                    <SvgIcon component={GearIcon} inheritViewBox />
                </IconButton>
            )}
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "setting-dropdown-menu"
                    }
                }}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                anchorOrigin={{ horizontal: "left", vertical: "center" }}
            >
                {actions.map(action => (
                    <div
                        key={action.name}
                        className={`setting-dropdown-menu-item${action.type == SettingItemType.dangerous ? " setting-dropdown-menu-item--dangerous" : ""}`}
                        onClick={() => handleMenuItem(action.handler)}
                    >
                        <SvgIcon
                            className="setting-dropdown-menu-item__icon"
                            component={action.icon}
                            inheritViewBox
                        />
                        <span className="setting-dropdown-menu-item__title">{action.name}</span>
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

export interface DropdownMenuItem {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    handler: () => void;
}

export const DropdownMenu = ({
    icon,
    title,
    actions
}: {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title?: string;
    actions: DropdownMenuItem[];
}) => {
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuItem = (handler: () => void) => {
        handler();
        handleMenuClose();
    };

    return (
        <Fragment>
            <Button className="dropdown-menu-button" onClick={handleMenuClick}>
                <SvgIcon className="dropdown-menu-button__icon" component={icon} inheritViewBox />
                {title && <span className="dropdown-menu-button__title">{title}</span>}
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "setting-dropdown-menu"
                    }
                }}
                transformOrigin={{ horizontal: "left", vertical: "center" }}
                anchorOrigin={{ horizontal: "right", vertical: "center" }}
            >
                {actions.map(action => (
                    <div
                        key={action.title}
                        className="setting-dropdown-menu-item"
                        onClick={() => handleMenuItem(action.handler)}
                    >
                        <SvgIcon
                            className="setting-dropdown-menu-item__icon"
                            component={action.icon}
                            inheritViewBox
                        />
                        <span className="setting-dropdown-menu-item__title">{action.title}</span>
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};
